import firebase from 'firebase/app'
import api, { defaultHeaders } from '@/api'
import { ensureFirebaseAuth } from '@/api/firebase-auth'
import { str2Ascii } from '@/api/utils'
import { VRLOUNGE } from '@/api/constants'

const serverUrl = VRLOUNGE.SERVER

export const getLoungeRole = async loungeId => {
  try {
    const url = `${serverUrl}/api/user/addmission?lounge=${loungeId}`
    const resp = await api.asyncRequest(url, 'get')
    if (resp.ok && resp.data.success) return resp.data.extras.addmission
    else return Promise.reject(resp.data.extras)
  } catch (error) {
    Promise.reject(error)
  }
}

// payload = {id: loungeId, role}
export const setLoungeRole = async (payload) => {
  const url = `${serverUrl}/api/user/addmission`
  const method = 'post'
  const resp = await api.asyncRequest(url, method, payload)
  if (resp.ok && resp.data.success) return resp.data.extras.addmission
  else return Promise.reject(resp.data.extras)
}

// payload = {lounge: loungeId}
export const enterLounge = async (payload) => {
  const url = `${serverUrl}/api/user/enter`
  const method = 'post'
  const resp = await api.asyncRequest(url, method, payload)
  if (resp.ok && resp.data.success) return resp.data.extras.addmissionid
  else return Promise.reject(resp.data.extras)
}

export const getLoungeUsers = async (loungeId) => {
  const pageSize = 10000
  const page = 1
  const url = `${serverUrl}/api/lounge/people/${loungeId}?page=${page}&pageSize=${pageSize}`
  const method = 'get'
  const resp = await api.asyncRequest(url, method)
  if (resp.ok && resp.data.success) {
    return resp.data.extras
  } else {
    return Promise.reject(resp.data.extras)
  }
}

// payload = {receiver: userId, lounge: loungeId, message}
export const sendCard = async (payload) => {
  const url = `${serverUrl}/api/connect/invite`
  const method = 'post'
  const resp = await api.asyncRequest(url, method, payload)
  if (resp.ok && resp.data.success) return true
  else return Promise.reject(resp.data.extras)
}

// payload = {nid: cardId}
export const acceptCard = async (payload) => {
  const url = `${serverUrl}/api/connect/accept`
  const method = 'post'
  const resp = await api.asyncRequest(url, method, payload)
  if (resp.ok && resp.data.success) return true
  else return Promise.reject(resp.data.extras)
}

// payload = {nid: cardId}
export const rejectCard = async (payload) => {
  const url = `${serverUrl}/api/connect/reject`
  const method = 'post'
  const resp = await api.asyncRequest(url, method, payload)
  if (resp.ok && resp.data.success) return true
  else return Promise.reject(resp.data.extras)
}

// payload = {nid: cardId}
export const blockUser = async (payload) => {
  const url = `${serverUrl}/api/connect/blockade`
  const method = 'post'
  const resp = await api.asyncRequest(url, method, payload)
  if (resp.ok && resp.data.success) return true
  else return Promise.reject(resp.data.extras)
}

// payload = {nid: cardId}
export const unBlockUser = async (payload) => {
  const url = `${serverUrl}/api/connect/unblockade`
  const method = 'post'
  const resp = await api.asyncRequest(url, method, payload)
  if (resp.ok && resp.data.success) return true
  else return Promise.reject(resp.data.extras)
}

export const getContacts = async (loungeId) => {
  const url = `${serverUrl}/api/user/network/${loungeId}`
  const method = 'get'
  const resp = await api.asyncRequest(url, method)
  if (resp.ok && resp.data.success) return resp.data.extras.contacts
  else return Promise.reject(resp.data.extras)
}

// payload = {lounge: loungeId}
export const countVisitor = async (payload) => {
  const url = `${serverUrl}/api/lounge/click`
  const method = 'post'
  const resp = await api.asyncRequest(url, method, payload)
  if (resp.ok && resp.data.success) return true
  else return Promise.reject(resp.data.extras)
}

// to notice server that our vrmaker server user data is updated
export const noticeUserUpdate = async () => {
  const url = `${serverUrl}/api/user/update`
  const method = 'post'
  const resp = await api.asyncRequest(url, method)
  if (resp.ok && resp.data.success) return true
  else return Promise.reject(resp.data.extras)
}

export const getTalkingContacts = async (loungeId) => {
  const url = `${serverUrl}/api/user/talks/${loungeId}`
  const method = 'get'
  const resp = await api.asyncRequest(url, method)
  if (resp.ok && resp.data.success) return resp.data.extras.contacts
  else return Promise.reject(resp.data.extras)
}

export const verifyTicket = async (ticketNumber) => {
  const urlEnd = `/v2/helper/verifyTicket/${ticketNumber}`
  const method = 'get'
  const resp = await api.asyncRequest(urlEnd, method)
  if (resp.ok) return resp.data
  else return Promise.reject(resp.data)
}

export const recordTicketEmail = async ({ loungeId, email, role, ticket }) => {
  await ensureFirebaseAuth()
  const vrloungeRef = firebase.database().ref(VRLOUNGE.BASIC_FOLDER)
  const key = str2Ascii(email)
  return vrloungeRef.child(loungeId).child('email').child(key).set({ email, role, ticket, createdAt: Date.now() })
}

export const recordEmailTicket = async ({ loungeId, email, role, ticket }) => {
  await ensureFirebaseAuth()
  const vrloungeRef = firebase.database().ref(VRLOUNGE.BASIC_FOLDER)
  return vrloungeRef.child(loungeId).child('ticket').child(ticket).set({ email, role, ticket, createdAt: Date.now() })
}

export const verfifyTicketEmail = async ({ loungeId, email }) => {
  await ensureFirebaseAuth()
  const vrloungeRef = firebase.database().ref(VRLOUNGE.BASIC_FOLDER)
  const key = str2Ascii(email)
  const result = await vrloungeRef.child(loungeId).child('email').child(key).once('value').then(ss => ss.val())
  if (result) return result
  // eslint-disable-next-line prefer-promise-reject-errors
  else return Promise.reject(false)
}

export const getTicketRecord = async ({ loungeId, ticket }) => {
  await ensureFirebaseAuth()
  const vrloungeRef = firebase.database().ref(VRLOUNGE.BASIC_FOLDER)
  const result = await vrloungeRef.child(loungeId).child('ticket').child(ticket).once('value').then(ss => ss.val())
  return result
}

export const exportContacts = async (loungeId) => {
  const filename = 'contacts'
  const url = `${serverUrl}/api/user/network/export/${loungeId}?type=xlsx&filename=${filename}`
  const options = {
    headers: defaultHeaders
  }
  const resp = await fetch(url, options)
  const blob = await resp.blob()
  const objurl = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objurl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
}
